/**
 * ML Product — tabs, variant price sync, sticky mobile bar
 */
(function () {
  const VARIANT_UPDATE = 'variant:update';

  const getUpdatedRoot = (html, sectionId) => {
    return (
      html.querySelector(`#shopify-section-${sectionId} [data-ml-product]`) ||
      html.querySelector(`[data-section-id="${sectionId}"][data-ml-product]`)
    );
  };

  const syncInnerHtml = (root, sourceRoot, selector) => {
    const current = root.querySelector(selector);
    const updated = sourceRoot.querySelector(selector);
    if (!current || !updated) return;
    current.innerHTML = updated.innerHTML;
  };

  const scrollVtabsToContentStart = (vtabs) => {
    const panels = vtabs.querySelector('.ml-product__vtabs-panels');
    if (!panels) return;

    const gap = 12;
    const bodyStyles = getComputedStyle(document.body);
    const headerHeight =
      parseFloat(bodyStyles.getPropertyValue('--header-height')) ||
      parseFloat(bodyStyles.getPropertyValue('--header-group-height')) ||
      0;

    let offset = gap;
    const headerEl = document.querySelector('#header-component, header-component');
    const headerGroup = document.querySelector('#header-group');
    const stickyMode = headerEl?.getAttribute('sticky');

    if (headerEl) {
      const headerRect = headerEl.getBoundingClientRect();
      if (headerRect.bottom > 0) {
        offset = Math.max(offset, headerRect.bottom + gap);
      }
    }

    const scrolledPastHeader =
      headerGroup && window.scrollY > headerGroup.offsetHeight + 8;
    const headerStickySoon =
      stickyMode === 'scroll-up' &&
      (headerEl?.dataset.stickyState === 'active' ||
        headerEl?.dataset.stickyState === 'idle' ||
        scrolledPastHeader);
    const headerAlwaysSticky =
      stickyMode === 'always' && headerEl?.dataset.stickyState === 'active';

    if (headerHeight > 0 && (headerStickySoon || headerAlwaysSticky)) {
      offset = Math.max(offset, headerHeight + gap);
    }

    if (window.matchMedia('(max-width: 749px)').matches) {
      const nav = vtabs.querySelector('.ml-product__vtabs-nav');
      if (nav) offset += nav.offsetHeight;
    }

    const top = window.scrollY + panels.getBoundingClientRect().top - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  };

  const initProduct = (root) => {
    if (!root || root.dataset.mlProductInit === 'true') return;
    root.dataset.mlProductInit = 'true';

    root.querySelectorAll('[data-ml-vtabs]').forEach((vtabs) => {
      const tabButtons = vtabs.querySelectorAll('[data-ml-vtab]');
      const panels = vtabs.querySelectorAll('[data-ml-vpanel]');

      tabButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const id = button.getAttribute('data-ml-vtab');
          if (button.classList.contains('is-active')) return;

          tabButtons.forEach((btn) => {
            const active = btn === button;
            btn.classList.toggle('is-active', active);
            btn.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          panels.forEach((panel) => {
            const isActive = panel.getAttribute('data-ml-vpanel') === id;
            panel.classList.toggle('is-active', isActive);
            panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          });

          scrollVtabsToContentStart(vtabs);
        });
      });
    });

    const updateAvailability = (available, sku) => {
      const statusRow = root.querySelector('[data-ml-status-row]');
      const stockEl = root.querySelector('[data-ml-stock]');
      if (!statusRow || !stockEl) return;

      const inStockLabel = statusRow.dataset.inStockLabel || 'In Stock';
      const soldOutLabel = statusRow.dataset.soldOutLabel || 'Sold Out';
      const isAvailable = available === true;

      stockEl.textContent = isAvailable ? inStockLabel : soldOutLabel;
      stockEl.dataset.available = isAvailable ? 'true' : 'false';
      stockEl.classList.toggle('ml-product__stock--in', isAvailable);
      stockEl.classList.toggle('ml-product__stock--out', !isAvailable);

      const skuValue = root.querySelector('[data-ml-sku-value]');
      if (skuValue && typeof sku === 'string') {
        skuValue.textContent = sku;
      }
    };

    const onVariantUpdate = (event) => {
      const detail = event.detail;
      if (!detail?.data?.html) return;

      const productId = root.dataset.productId;
      if (productId && detail.data.productId && detail.data.productId !== productId) return;

      const sectionId = root.dataset.sectionId;
      if (!sectionId) return;

      const updatedRoot = getUpdatedRoot(detail.data.html, sectionId);
      if (!updatedRoot) return;

      syncInnerHtml(root, updatedRoot, '[data-ml-price-status]');
      syncInnerHtml(root, updatedRoot, '.ml-product__sticky-price');

      const stickyAtc = root.querySelector('[data-ml-sticky-atc]');
      const updatedStickyAtc = updatedRoot.querySelector('[data-ml-sticky-atc]');
      if (stickyAtc && updatedStickyAtc) {
        stickyAtc.disabled = updatedStickyAtc.disabled;
        stickyAtc.textContent = updatedStickyAtc.textContent;
      }

      // Keep status aligned with the selected variant (resource) as a fallback
      const resource = detail.resource;
      if (resource) {
        updateAvailability(Boolean(resource.available), resource.sku || '');
      } else {
        updateAvailability(false, '');
      }
    };

    root.addEventListener(VARIANT_UPDATE, onVariantUpdate);

    const stickyAtc = root.querySelector('[data-ml-sticky-atc]');
    const mainAtc = root.querySelector('.ml-product__atc');

    if (stickyAtc && mainAtc) {
      stickyAtc.addEventListener('click', () => {
        mainAtc.click();
      });
    }

    const buybox = root.querySelector('[data-ml-product-buybox]');
    const sticky = root.querySelector('[data-ml-product-sticky]');

    if (buybox && sticky && window.matchMedia('(max-width: 989px)').matches) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          sticky.hidden = entry.isIntersecting;
        },
        { threshold: 0, rootMargin: '0px 0px -80px 0px' }
      );
      observer.observe(buybox);
    }
  };

  const boot = () => {
    document.querySelectorAll('[data-ml-product]').forEach(initProduct);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target?.querySelector?.('[data-ml-product]');
    if (root) initProduct(root);
  });
})();
